import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hotelService, fileService } from "@/services/ServiceRegistry";
import {
  Hotel as HotelBrand,
  CreateHotelDto,
  UpdateHotelDto,
  HotelFilters,
} from "@/types/hotel";
import { HotelFormData } from "../utils/hotelSchema";

export const hotelKeys = {
  all: ["hotels"] as const,
  lists: () => [...hotelKeys.all, "list"] as const,
  list: (filters?: HotelFilters) => [...hotelKeys.lists(), filters] as const,
  details: () => [...hotelKeys.all, "detail"] as const,
  detail: (id: string) => [...hotelKeys.details(), id] as const,
};

export const useHotels = (filters?: HotelFilters, enabled = true) => {
  return useQuery({
    queryKey: hotelKeys.list(filters),
    queryFn: () => hotelService.getHotels(filters),
    staleTime: 5 * 60 * 1000,
    enabled,
    placeholderData: (previousData) => previousData,
  });
};

export const useCreateHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelData: CreateHotelDto) =>
      hotelService.createHotel(hotelData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
    },
  });
};

export const useUpdateHotel = () => {
  const queryClient = useQueryClient();

  return useMutation<HotelBrand, Error, { id: string; data: UpdateHotelDto }>({
    mutationFn: ({ id, data }: { id: string; data: UpdateHotelDto }) =>
      hotelService.updateHotel(id, data),
    onSuccess: (response: HotelBrand) => {
      queryClient.setQueryData(hotelKeys.detail(response.id), response);
      queryClient.setQueriesData<HotelBrand | null>(
        { queryKey: ["userHotel"] },
        (current) =>
          current?.id === response.id ? { ...current, ...response } : current,
      );
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};

export const useDeleteHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelId: string) => hotelService.deleteHotel(hotelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
    },
  });
};

export const useActivateHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelId: string) => hotelService.activateHotel(hotelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};

export const useDeactivateHotel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (hotelId: string) => hotelService.deactivateHotel(hotelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};

export const useHotelCreate = () => {
  const createHotelMutation = useCreateHotel();

  const handleHotelCreate = async (
    hotelData: HotelFormData,
    logoFile?: File,
    mapFile?: File,
  ) => {
    try {
      let logoFileId: string | undefined;

      if (logoFile) {
        try {
          logoFileId = await fileService.uploadFileWithPresignedUrl(logoFile);
        } catch (logoError) {
          console.error("Failed to upload logo:", logoError);
        }
      }

      let mapFileId: string | undefined;

      if (mapFile) {
        try {
          mapFileId = await fileService.uploadFileWithPresignedUrl(mapFile);
        } catch (error) {
          console.error("Failed to upload map:", error);
        }
      }

      const createHotelData: CreateHotelDto = {
        ...hotelData,
        logoId: logoFileId!,
        mapFileId,
      };

      await createHotelMutation.mutateAsync(createHotelData);
    } catch (error) {
      console.error("Failed to create hotel:", error);
      throw error;
    }
  };

  return {
    handleHotelSubmit: handleHotelCreate,
    isLoading: createHotelMutation.isPending,
    error: createHotelMutation.error,
  };
};

export const useHotelUpdate = (hotelId: string) => {
  const updateHotelMutation = useUpdateHotel();

  const handleHotelUpdate = async (
    hotelData: HotelFormData,
    logoFile?: File,
    currentLogoId?: string,
    mapFile?: File,
    currentMapId?: string,
  ) => {
    try {
      let logoFileId: string | undefined = currentLogoId;

      if (logoFile) {
        try {
          logoFileId = await fileService.uploadFileWithPresignedUrl(logoFile);
        } catch (error) {
          console.error("Failed to upload logo:", error);
        }
      }

      let mapFileId: string | undefined = currentMapId;

      if (mapFile) {
        try {
          mapFileId = await fileService.uploadFileWithPresignedUrl(mapFile);
        } catch (error) {
          console.error("Failed to upload map:", error);
        }
      }

      const { managerIds, ...hotelFields } = hotelData;
      const updateHotelData: UpdateHotelDto = {
        ...hotelFields,
        ...(managerIds.length ? { managerIds } : {}),
        logoId: logoFileId,
        mapFileId,
      };

      await updateHotelMutation.mutateAsync({
        id: hotelId,
        data: updateHotelData,
      });
    } catch (error) {
      console.error("Failed to update hotel:", error);
      throw error;
    }
  };

  return {
    handleHotelSubmit: handleHotelUpdate,
    isLoading: updateHotelMutation.isPending,
    error: updateHotelMutation.error,
  };
};
